import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  RoleGuard,
  Table,
  usePermission,
  useTable,
} from 'portal-commons';
import React, { useMemo, FunctionComponent } from 'react';

import { DetailsSection, Divider } from './components';
import ScoreTooltip from '../BrandVettingDetailsBlock/ScoreTooltip';
import { getBadgeTypeByStatus } from '../../utils';
import { VettingRecord, brandController } from '@/apis/core';
import MoreButton from '@/shared_elements/components/MoreButton';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { resend2FA } from '@/applications/CspPortal/Brands/apis';
import { toastFlashMessage } from '@/utils';

interface Authplus2faTabProps {
  readOnly?: boolean;
  brandUid: string;
  onApply?: () => void;
}

const TableHeaders = [
  {
    id: 'vettingClass',
    label: 'Verification Type',
    sortable: false,
  },
  {
    id: 'vettedDate',
    label: 'Verified On',
    sortable: false,
  },
  {
    id: 'vettingStatus',
    label: 'Verification Status',
    sortable: false,
  },
];

const Authplus2faTab: FunctionComponent<Authplus2faTabProps> = ({
  readOnly,
  brandUid,
  onApply,
}) => {
  const { hasPermission } = usePermission();
  const { loading: tableLoading, tableData: records } = useTable<VettingRecord>(
    async () => {
      const records = brandUid
        ? await brandController.getVettingRecords(brandUid, {
            vettingClass: 'AUTHPLUS',
          })
        : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    {
      deps: [brandUid],
      inMemory: true,
    }
  );

  const handleResend2fa = async (record: VettingRecord) => {
    const response = await resend2FA(record.brandUid, {
      vettingClass: record.vettingClass,
    });
    if (response) {
      toastFlashMessage(
        'Auth+ 2FA verification email resent successfully.',
        'success'
      );
    }
  };

  const tableData = useMemo(() => {
    return records.map((record) => ({
      vettingClass: (
        <span data-testid="vettingClass">{record.vettingClass || 'N/A'}</span>
      ),
      vettingStatus: (
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          {humanize(record.vettingStatus) ? (
            <Badge
              type={getBadgeTypeByStatus(record.vettingStatus)}
              endAdornment={
                <Box
                  sx={{
                    '& .view-icon': {
                      color: 'inherit !important',
                      fontSize: '10px !important',
                    },
                    '& .view-icon > div': {
                      width: '12px',
                      margin: '0 !important',
                    },
                    '& .pointer.copy': {
                      pointerEvents: 'auto',
                    },
                  }}
                >
                  <ScoreTooltip data={record} />
                </Box>
              }
              data-testid="vettingStatus"
            >
              {humanize(record.vettingStatus)}
            </Badge>
          ) : (
            <span data-testid="vettingStatus">N/A</span>
          )}
          <MoreButton
            menuClass="tcr-menu"
            data={record}
            items={[
              {
                disabled:
                  !hasPermission('brandDetail.send2faEmail') ||
                  record.vettingStatus !== 'PENDING',
                label: 'Resend 2FA',
                onClick: handleResend2fa,
              },
            ]}
            data-testid="showMore"
          />
        </Flex>
      ),
      vettedDate: (
        <span data-testid="vettedDate">
          {record.vettedDate
            ? convertTimeWithTimezone(
                record.vettedDate,
                undefined,
                'MM/DD/YYYY - hh:mm z'
              )
            : 'N/A'}
        </span>
      ),
    }));
  }, [records]);

  return (
    <>
      <DetailsSection description="Authentication+ verification must be completed before sending a letter of authorization.">
        <Table
          disableHover
          loading={tableLoading}
          headRows={TableHeaders}
          data={tableData}
          emptyState="no vettings to view"
          emptyBlockHeight="180px"
        />
      </DetailsSection>
      <Divider />
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '24px',
        }}
      >
        <RoleGuard feature="brandDetail.applyForVetting">
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            disabled={readOnly}
            onClick={onApply}
            data-testid="applyVerification"
          >
            Apply for Verification
          </Button>
        </RoleGuard>
      </Flex>
    </>
  );
};

export default Authplus2faTab;
